/**
 * PopupComposer — Dialog used by admins & account managers to compose a
 * client-facing dashboard popup (info / warning / freeze alert).
 *
 * The dialog also lists previously-sent popups so senders can un-send.
 */
import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Trash2, Send, AlertTriangle, Info, ShieldAlert, Eye, EyeOff, Check } from "lucide-react";
import { toast } from "sonner";

const TONES = [
  { value: "info",    label: "Info",    icon: Info,        ring: "border-sky-800 text-sky-300" },
  { value: "warning", label: "Warning", icon: AlertTriangle, ring: "border-amber-800 text-amber-300" },
  { value: "danger",  label: "Danger",  icon: ShieldAlert, ring: "border-red-800 text-red-300" },
];

export default function PopupComposer({ open, onOpenChange, client, engagementId }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [tone, setTone] = useState("info");
  const [dismissible, setDismissible] = useState(true);
  const [isFreeze, setIsFreeze] = useState(false);
  const [expiresAt, setExpiresAt] = useState("");
  const [busy, setBusy] = useState(false);
  const [list, setList] = useState([]);

  const clientRef = client?.slug || client?.id;

  const reload = async () => {
    if (!clientRef) return;
    try {
      const r = await api.get(`/clients/${clientRef}/popups`);
      setList(r.data || []);
    } catch { /* silent */ }
  };

  useEffect(() => {
    if (open) reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, clientRef]);

  const send = async () => {
    if (!title.trim() || !body.trim()) { toast.error("Title and body are required"); return; }
    if (isFreeze && !engagementId) { toast.error("No engagement to freeze. Create one first."); return; }
    setBusy(true);
    try {
      await api.post(`/clients/${clientRef}/popups`, {
        title, body, tone, dismissible,
        is_freeze: isFreeze,
        engagement_id: isFreeze ? engagementId : null,
        expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
      });
      toast.success("Popup sent");
      setTitle(""); setBody(""); setTone("info");
      setDismissible(true); setIsFreeze(false); setExpiresAt("");
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send");
    } finally { setBusy(false); }
  };

  const remove = async (pid) => {
    if (!window.confirm("Remove this popup? Clients will no longer see it.")) return;
    try {
      await api.delete(`/clients/${clientRef}/popups/${pid}`);
      await reload();
      toast.success("Removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-zinc-800 max-w-lg" data-testid="popup-composer">
        <DialogHeader>
          <DialogTitle className="text-zinc-100 text-[15px] font-display font-semibold">Send popup to client</DialogTitle>
          <DialogDescription className="text-zinc-500 text-[11px]">
            Compose an in-dashboard notification that only the client role will see. Optionally freeze the engagement on dismiss.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3.5">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder="Payment reminder"
              className="bg-[#0a0a0a] border-zinc-800 text-[13px] h-9 mt-1"
              data-testid="popup-title" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">Message</Label>
            <Textarea value={body} onChange={(e) => setBody(e.target.value)}
              rows={3}
              placeholder="Milestone 2 payment is due Friday. Please clear it to keep the dashboard active."
              className="bg-[#0a0a0a] border-zinc-800 text-[13px] mt-1"
              data-testid="popup-body" />
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">Tone</Label>
            <div className="flex gap-2 mt-1" data-testid="popup-tone">
              {TONES.map((t) => (
                <button key={t.value} type="button" onClick={() => setTone(t.value)}
                  data-testid={`popup-tone-${t.value}`}
                  className={`flex items-center gap-1.5 px-3 h-8 rounded border text-[11px] transition-all ${
                    tone === t.value ? `bg-[#111] ${t.ring}` : "bg-transparent text-zinc-500 border-zinc-800 hover:border-zinc-600"
                  }`}>
                  <t.icon className="w-3 h-3" /> {t.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">Expires (optional)</Label>
              <Input type="datetime-local" value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                className="bg-[#0a0a0a] border-zinc-800 text-[12px] h-9 mt-1"
                data-testid="popup-expires" />
            </div>
            <div className="flex flex-col justify-end gap-1.5 text-[11px] text-zinc-400">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={dismissible} onChange={(e) => setDismissible(e.target.checked)}
                  data-testid="popup-dismissible" />
                Client can dismiss
              </label>
              <label className={`flex items-center gap-2 cursor-pointer ${!engagementId ? "opacity-40 cursor-not-allowed" : ""}`}
                title={!engagementId ? "Create an engagement to enable freeze" : ""}>
                <input type="checkbox" checked={isFreeze} disabled={!engagementId}
                  onChange={(e) => setIsFreeze(e.target.checked)} data-testid="popup-is-freeze" />
                Freeze on dismiss
              </label>
            </div>
          </div>

          <div className="flex gap-2 justify-end pt-1">
            <Button size="sm" variant="ghost" onClick={() => onOpenChange(false)} className="text-zinc-500 h-8 text-[12px]">Cancel</Button>
            <Button size="sm" onClick={send} disabled={busy} className="bg-white text-black hover:bg-zinc-200 h-8 text-[12px]" data-testid="popup-send">
              <Send className="w-3 h-3 mr-1.5" /> {busy ? "Sending…" : "Send popup"}
            </Button>
          </div>

          {list.length > 0 && (
            <div className="pt-3 border-t border-zinc-900">
              <div className="t-eyebrow mb-2">Recent popups ({list.length})</div>
              <ul className="space-y-2 max-h-64 overflow-y-auto pr-1" data-testid="popup-recent-list">
                {list.map((p) => {
                  const views = p.views_detail || [];
                  const dismissed = p.dismissed_detail || [];
                  const dismissedIds = new Set(dismissed.map((d) => d.user_id));
                  const viewedNotDismissed = views.filter((v) => !dismissedIds.has(v.user_id));
                  return (
                    <li key={p.id} className="bg-[#0a0a0a] border border-zinc-900 rounded p-2.5 text-[12px]"
                      data-testid={`popup-row-${p.id}`}>
                      <div className="flex items-start gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="text-zinc-200 truncate">{p.title}</div>
                          <div className="text-zinc-500 text-[11px] truncate">{p.body}</div>
                          <div className="text-[10px] text-zinc-600 font-mono mt-0.5">
                            {p.tone} · {p.is_freeze ? "freeze" : "info"}
                          </div>
                        </div>
                        <button onClick={() => remove(p.id)}
                          className="text-zinc-500 hover:text-[#EF4444] p-1 shrink-0"
                          title="Remove"
                          data-testid={`popup-remove-${p.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>

                      {(views.length + dismissed.length) > 0 && (
                        <div className="mt-2 pt-2 border-t border-zinc-900 space-y-1" data-testid={`popup-receipts-${p.id}`}>
                          {dismissed.length > 0 && (
                            <div className="flex items-start gap-1.5 text-[11px]">
                              <Check className="w-3 h-3 text-emerald-400 mt-0.5 shrink-0" />
                              <div className="flex-1 min-w-0">
                                <div className="text-emerald-300 text-[10px] font-mono uppercase tracking-wider">
                                  Acknowledged · {dismissed.length}
                                </div>
                                {dismissed.map((d) => (
                                  <div key={d.user_id} className="text-zinc-400 truncate"
                                    data-testid={`popup-dismissed-${p.id}-${d.user_id}`}>
                                    <span className="text-zinc-200">{d.name}</span>
                                    <span className="text-zinc-600 ml-1 font-mono">
                                      {d.at ? `· ${new Date(d.at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}` : ""}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {viewedNotDismissed.length > 0 && (
                            <div className="flex items-start gap-1.5 text-[11px]">
                              <Eye className="w-3 h-3 text-sky-400 mt-0.5 shrink-0" />
                              <div className="flex-1 min-w-0">
                                <div className="text-sky-300 text-[10px] font-mono uppercase tracking-wider">
                                  Seen · not acknowledged · {viewedNotDismissed.length}
                                </div>
                                {viewedNotDismissed.map((v) => (
                                  <div key={v.user_id} className="text-zinc-400 truncate"
                                    data-testid={`popup-viewed-${p.id}-${v.user_id}`}>
                                    <span className="text-zinc-200">{v.name}</span>
                                    <span className="text-zinc-600 ml-1 font-mono">
                                      · {v.count}× · last {v.last_at ? new Date(v.last_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      {(views.length + dismissed.length) === 0 && (
                        <div className="mt-2 pt-2 border-t border-zinc-900 flex items-center gap-1.5 text-[11px] text-zinc-600" data-testid={`popup-no-receipts-${p.id}`}>
                          <EyeOff className="w-3 h-3" /> No client has opened this yet.
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
